import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { Switch } from './ui/switch';
import { 
  Plus, 
  Trash2, 
  Edit2, 
  Save, 
  X, 
  ChevronDown, 
  ChevronUp,
  Loader2,
  CheckCircle2,
  GripVertical,
  Target,
  RotateCcw,
  Users,
  BarChart3,
  Archive,
  Eye,
  EyeOff
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

const API_URL = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

// Get user from localStorage
const getUser = () => {
  try {
    const storedUser = localStorage.getItem('exlva_dare_user');
    return storedUser ? JSON.parse(storedUser) : null;
  } catch {
    return null;
  }
};

const PurposeManagement = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [user] = useState(getUser);
  
  const [categories, setCategories] = useState([]);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [showArchived, setShowArchived] = useState(false); // Toggle for archived items
  
  // Edit/Create state
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  const [showItemDialog, setShowItemDialog] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  
  // Form state
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    description: '',
    icon: '📋'
  });
  const [itemForm, setItemForm] = useState({
    name: '',
    description: ''
  });
  
  useEffect(() => {
    loadCategories();
  }, []);
  
  const loadCategories = async () => {
    setError('');
    try {
      if (!user?.id) {
        setError('User session not found. Please log in again.');
        setLoading(false);
        return;
      }
      
      const response = await axios.get(
        `${API_URL}/admin/purpose-categories?user_id=${user.id}`
      );
      setCategories(response.data.categories || []);
      
      // Expand all categories by default
      const expanded = {};
      (response.data.categories || []).forEach(cat => { expanded[cat.id] = true; });
      setExpandedCategories(expanded);
    } catch (err) {
      console.error('Error loading categories:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to load AI insight categories';
      setError(`Failed to load AI insight categories: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };
  
  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };
  
  const handleCreateCategory = () => {
    setCategoryForm({ name: '', description: '', icon: '📋' });
    setEditingCategory(null);
    setShowCategoryDialog(true);
  };
  
  const handleEditCategory = (category) => {
    setCategoryForm({
      name: category.name,
      description: category.description || '',
      icon: category.icon || '📋'
    });
    setEditingCategory(category);
    setShowCategoryDialog(true);
  };
  
  const handleSaveCategory = async () => {
    if (!categoryForm.name.trim()) {
      setError('Category name is required');
      return;
    }
    
    if (!user?.id) {
      setError('User session not found. Please log in again.');
      return;
    }
    
    setSaving(true);
    setError('');
    setSuccess('');
    
    try {
      if (editingCategory) {
        // Update existing category
        await axios.put(
          `${API_URL}/admin/purpose-categories/${editingCategory.id}?user_id=${user.id}`,
          categoryForm
        );
        setSuccess('Category updated successfully');
      } else {
        // Create new category
        await axios.post(
          `${API_URL}/admin/purpose-categories?user_id=${user.id}`,
          { ...categoryForm, items: [] }
        );
        setSuccess('Category created successfully');
      }
      
      // Close dialog and refresh on success
      setShowCategoryDialog(false);
      setCategoryForm({ name: '', description: '', icon: '📋' });
      setEditingCategory(null);
      await loadCategories();
      setTimeout(() => setSuccess(''), 3000);
      
    } catch (err) {
      console.error('Error saving category:', err);
      // Extract error message properly
      const errorMsg = err.response?.data?.detail 
        || err.response?.data?.message 
        || err.message 
        || 'Failed to save category';
      setError(typeof errorMsg === 'string' ? errorMsg : 'Failed to save category');
      // Keep dialog open on error
    } finally {
      setSaving(false);
    }
  };
  
  const handleToggleCategoryEnabled = async (category) => {
    try {
      await axios.put(
        `${API_URL}/admin/purpose-categories/${category.id}?user_id=${user.id}`,
        { is_enabled: !category.is_enabled }
      );
      loadCategories();
    } catch (err) {
      console.error('Error toggling category:', err);
      setError('Failed to update category');
    }
  };
  
  const handleDeleteCategory = async (category) => {
    const hasUsage = (category.total_selections || 0) > 0;
    const confirmMessage = hasUsage 
      ? `"${category.name}" has been selected ${category.total_selections} time(s) by users. It will be archived (not permanently deleted) to preserve usage history. Continue?`
      : `"${category.name}" has never been selected by any user. It will be permanently deleted. Continue?`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }
    try {
      const response = await axios.delete(
        `${API_URL}/admin/purpose-categories/${category.id}?user_id=${user.id}`
      );
      setSuccess(response.data?.message || 'Category deleted successfully');
      loadCategories();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error deleting category:', err);
      setError(err.response?.data?.detail || 'Failed to delete category');
    }
  };
  
  const handleRestoreCategory = async (category) => {
    try {
      await axios.post(
        `${API_URL}/admin/purpose-categories/${category.id}/restore?user_id=${user.id}`
      );
      setSuccess('Category restored successfully');
      loadCategories();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error restoring category:', err);
      setError(err.response?.data?.detail || 'Failed to restore category');
    }
  };
  
  const handleDeleteItem = async (category, item) => {
    const hasUsage = (item.total_selections || 0) > 0;
    const confirmMessage = hasUsage 
      ? `"${item.name}" has been selected ${item.total_selections} time(s) by users. It will be archived (not permanently deleted) to preserve usage history. Continue?`
      : `"${item.name}" has never been selected by any user. It will be permanently deleted. Continue?`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }
    try {
      const response = await axios.delete(
        `${API_URL}/admin/purpose-categories/${category.id}/items/${item.id}?user_id=${user.id}`
      );
      setSuccess(response.data?.message || 'Objective deleted successfully');
      loadCategories();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error deleting objective:', err);
      setError(err.response?.data?.detail || 'Failed to delete objective');
    }
  };
  
  const handleRestoreItem = async (category, item) => {
    try {
      await axios.post(
        `${API_URL}/admin/purpose-categories/${category.id}/items/${item.id}/restore?user_id=${user.id}`
      );
      setSuccess('Objective restored successfully');
      loadCategories();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error restoring objective:', err);
      setError(err.response?.data?.detail || 'Failed to restore objective');
    }
  };
  
  const handleAddItem = (categoryId) => {
    setItemForm({ name: '', description: '' });
    setEditingItem(null);
    setSelectedCategoryId(categoryId);
    setShowItemDialog(true);
  };
  
  const handleEditItem = (category, item) => {
    setItemForm({
      name: item.name,
      description: item.description || ''
    });
    setEditingItem(item);
    setSelectedCategoryId(category.id);
    setShowItemDialog(true);
  };
  
  const handleSaveItem = async () => {
    if (!itemForm.name.trim()) {
      setError('Objective name is required');
      return;
    }
    
    if (!user?.id) {
      setError('User session not found. Please log in again.');
      return;
    }
    
    setSaving(true);
    setError('');
    setSuccess('');
    
    try {
      if (editingItem) {
        // Update existing item
        await axios.put(
          `${API_URL}/admin/purpose-categories/${selectedCategoryId}/items/${editingItem.id}?user_id=${user.id}`,
          itemForm
        );
        setSuccess('Insight objective updated successfully');
      } else {
        // Add new item
        await axios.post(
          `${API_URL}/admin/purpose-categories/${selectedCategoryId}/items?user_id=${user.id}`,
          { name: itemForm.name, description: itemForm.description }
        );
        setSuccess('Insight objective added successfully');
      }
      
      // Close dialog and refresh on success
      setShowItemDialog(false);
      setItemForm({ name: '', description: '' });
      setEditingItem(null);
      setSelectedCategoryId(null);
      await loadCategories();
      setTimeout(() => setSuccess(''), 3000);
      
    } catch (err) {
      console.error('Error saving objective:', err);
      // Extract error message properly
      const errorMsg = err.response?.data?.detail 
        || err.response?.data?.message 
        || err.message 
        || 'Failed to save insight objective';
      setError(typeof errorMsg === 'string' ? errorMsg : 'Failed to save insight objective');
      // Keep dialog open on error
    } finally {
      setSaving(false);
    }
  };
  
  const handleToggleItemEnabled = async (category, item) => {
    try {
      await axios.put(
        `${API_URL}/admin/purpose-categories/${category.id}/items/${item.id}?user_id=${user.id}`,
        { is_enabled: !item.is_enabled }
      );
      loadCategories();
    } catch (err) {
      console.error('Error toggling objective:', err);
      setError('Failed to update insight objective');
    }
  };
  
  const commonEmojis = ['📋', '🏥', '✅', '📊', '🔬', '🌐', '📚', '🤖', '💊', '🩺', '📈', '⚕️', '🧪', '📁'];
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-red-600" />
      </div>
    );
  }
  
  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Target className="h-6 w-6 text-red-600" />
            AI Insight Categories
            {showArchived && (
              <Badge variant="secondary" className="ml-2">
                <Archive className="h-3 w-3 mr-1" />
                Archived View
              </Badge>
            )}
          </h1>
          <p className="text-gray-600">
            {showArchived 
              ? 'Viewing archived (deleted) categories and objectives'
              : 'Manage AI insight categories and objectives shown in the setup wizard'
            }
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Archive Toggle */}
          {(() => {
            const archivedCount = categories.filter(c => c.is_deleted).length;
            const archivedItemsCount = categories.reduce((acc, c) => 
              acc + (c.items || []).filter(i => i.is_deleted).length, 0
            );
            const totalArchived = archivedCount + archivedItemsCount;
            
            return (
              <Button 
                variant={showArchived ? "default" : "outline"} 
                size="sm"
                onClick={() => setShowArchived(!showArchived)}
                className={showArchived ? "bg-gray-600 hover:bg-gray-700" : ""}
              >
                {showArchived ? (
                  <>
                    <Eye className="h-4 w-4 mr-2" />
                    Show Active
                  </>
                ) : (
                  <>
                    <Archive className="h-4 w-4 mr-2" />
                    Show Archived
                    {totalArchived > 0 && (
                      <Badge variant="secondary" className="ml-2 bg-red-100 text-red-700">
                        {totalArchived}
                      </Badge>
                    )}
                  </>
                )}
              </Button>
            );
          })()}
          {!showArchived && (
            <Button onClick={handleCreateCategory} className="bg-red-600 hover:bg-red-700">
              <Plus className="h-4 w-4 mr-2" />
              Add Category
            </Button>
          )}
        </div>
      </div>
      
      {/* Alerts */}
      {error && (
        <Alert className="mb-4 border-red-200 bg-red-50">
          <AlertDescription className="text-red-800">{error}</AlertDescription>
        </Alert>
      )}
      {success && (
        <Alert className="mb-4 border-green-200 bg-green-50">
          <AlertDescription className="text-green-800 flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4" />
            {success}
          </AlertDescription>
        </Alert>
      )}
      
      {/* Categories List - Filter based on showArchived toggle */}
      <div className="space-y-4">
        {categories
          .filter(category => showArchived ? category.is_deleted : !category.is_deleted)
          .map((category) => (
          <Card key={category.id} className={`${!category.is_enabled || category.is_deleted ? 'opacity-60 border-dashed' : ''} ${category.is_deleted ? 'bg-red-50' : ''}`}>
            <CardHeader className="py-3">
              <div className="flex items-center justify-between">
                <div 
                  className="flex items-center gap-3 cursor-pointer flex-1"
                  onClick={() => toggleCategory(category.id)}
                >
                  <span className="text-2xl">{category.icon}</span>
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2">
                      {category.name}
                      {category.is_deleted && (
                        <Badge variant="destructive">Deleted</Badge>
                      )}
                      {!category.is_deleted && !category.is_enabled && (
                        <Badge variant="secondary">Disabled</Badge>
                      )}
                    </CardTitle>
                    {category.description && (
                      <CardDescription className="text-sm">
                        {category.description}
                      </CardDescription>
                    )}
                  </div>
                  <Badge variant="outline" className="ml-2">
                    {category.items?.length || 0} items
                  </Badge>
                  {/* Usage Stats */}
                  <div className="flex items-center gap-3 ml-4 text-xs text-gray-500">
                    <span className="flex items-center gap-1" title="Total selections">
                      <BarChart3 className="h-3 w-3" />
                      {category.total_selections || 0}
                    </span>
                    <span className="flex items-center gap-1" title="Unique users">
                      <Users className="h-3 w-3" />
                      {category.unique_users || 0}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {!category.is_deleted && (
                    <>
                      <Switch
                        checked={category.is_enabled}
                        onCheckedChange={() => handleToggleCategoryEnabled(category)}
                      />
                      <Button variant="ghost" size="sm" onClick={() => handleEditCategory(category)}>
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => handleDeleteCategory(category)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        title={(category.total_selections || 0) > 0 ? "Archive (has usage history)" : "Delete permanently"}
                      >
                        {(category.total_selections || 0) > 0 ? (
                          <Archive className="h-4 w-4" />
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                      </Button>
                    </>
                  )}
                  {category.is_deleted && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => handleRestoreCategory(category)}
                      className="text-green-600 hover:text-green-700"
                    >
                      <RotateCcw className="h-4 w-4 mr-1" />
                      Restore
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" onClick={() => toggleCategory(category.id)}>
                    {expandedCategories[category.id] ? (
                      <ChevronUp className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>
            </CardHeader>
            
            {expandedCategories[category.id] && (
              <CardContent className="pt-0">
                <div className="border-t pt-3">
                  {/* Items list - Filter based on showArchived toggle */}
                  <div className="space-y-2 mb-3">
                    {(category.items || [])
                      .filter(item => showArchived ? item.is_deleted : !item.is_deleted)
                      .map((item) => (
                      <div 
                        key={item.id} 
                        className={`flex items-center justify-between p-2 rounded-md ${
                          item.is_deleted ? 'bg-red-50 border border-dashed border-red-200' : 'bg-gray-50'
                        } ${!item.is_enabled || item.is_deleted ? 'opacity-60' : ''}`}
                      >
                        <div className="flex items-center gap-2 flex-1">
                          <GripVertical className="h-4 w-4 text-gray-400" />
                          <span className="text-sm">{item.name}</span>
                          {item.is_deleted && (
                            <Badge variant="destructive" className="text-xs">Deleted</Badge>
                          )}
                          {!item.is_deleted && !item.is_enabled && (
                            <Badge variant="secondary" className="text-xs">Disabled</Badge>
                          )}
                          {/* Item Usage Stats */}
                          <div className="flex items-center gap-2 ml-auto mr-4 text-xs text-gray-500">
                            <span className="flex items-center gap-1" title="Total selections">
                              <BarChart3 className="h-3 w-3" />
                              {item.total_selections || 0}
                            </span>
                            <span className="flex items-center gap-1" title="Unique users">
                              <Users className="h-3 w-3" />
                              {item.unique_users || 0}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          {!item.is_deleted && (
                            <>
                              <Switch
                                checked={item.is_enabled !== false}
                                onCheckedChange={() => handleToggleItemEnabled(category, item)}
                              />
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => handleEditItem(category, item)}
                              >
                                <Edit2 className="h-3 w-3" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => handleDeleteItem(category, item)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                title={(item.total_selections || 0) > 0 ? "Archive (has usage history)" : "Delete permanently"}
                              >
                                {(item.total_selections || 0) > 0 ? (
                                  <Archive className="h-3 w-3" />
                                ) : (
                                  <Trash2 className="h-3 w-3" />
                                )}
                              </Button>
                            </>
                          )}
                          {item.is_deleted && (
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={() => handleRestoreItem(category, item)}
                              className="text-green-600 hover:text-green-700 text-xs"
                            >
                              <RotateCcw className="h-3 w-3 mr-1" />
                              Restore
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Add item button */}
                  {!category.is_deleted && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => handleAddItem(category.id)}
                      className="w-full"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Insight Objective
                    </Button>
                  )}
                </div>
              </CardContent>
            )}
          </Card>
        ))}
        
        {/* Empty state - context aware */}
        {categories.filter(category => showArchived ? category.is_deleted : !category.is_deleted).length === 0 && (
          <div className="text-center py-12 text-gray-500">
            {showArchived ? (
              <>
                <Archive className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p>No archived categories found.</p>
                <p className="text-sm mt-1">Deleted categories will appear here.</p>
              </>
            ) : (
              <>
                <Target className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p>No AI insight categories found.</p>
                <p className="text-sm mt-1">Click "Add Category" to create one.</p>
              </>
            )}
          </div>
        )}
      </div>
      
      {/* Category Dialog */}
      <Dialog open={showCategoryDialog} onOpenChange={setShowCategoryDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingCategory ? 'Edit Category' : 'Create Category'}
            </DialogTitle>
            <DialogDescription>
              {editingCategory 
                ? 'Update the category details below.'
                : 'Enter the details for the new AI insight category.'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Icon</Label>
              <div className="flex flex-wrap gap-2">
                {commonEmojis.map((emoji) => (
                  <button
                    key={emoji}
                    type="button"
                    className={`w-10 h-10 text-xl rounded-md border-2 transition-colors ${
                      categoryForm.icon === emoji 
                        ? 'border-red-500 bg-red-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => setCategoryForm({ ...categoryForm, icon: emoji })}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="categoryName">Category Name *</Label>
              <Input
                id="categoryName"
                value={categoryForm.name}
                onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                placeholder="e.g., Clinical Research"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="categoryDesc">Description</Label>
              <Input
                id="categoryDesc"
                value={categoryForm.description}
                onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                placeholder="Brief description of this category"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCategoryDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveCategory} 
              disabled={saving}
              className="bg-red-600 hover:bg-red-700"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Item Dialog */}
      <Dialog open={showItemDialog} onOpenChange={setShowItemDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingItem ? 'Edit Insight Objective' : 'Add Insight Objective'}
            </DialogTitle>
            <DialogDescription>
              {editingItem 
                ? 'Update the insight objective details.'
                : 'Enter the details for the new insight objective.'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="itemName">Objective Name *</Label>
              <Input
                id="itemName"
                value={itemForm.name}
                onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })}
                placeholder="e.g., Clinical Trials Support"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="itemDesc">Description (Optional)</Label>
              <Input
                id="itemDesc"
                value={itemForm.description}
                onChange={(e) => setItemForm({ ...itemForm, description: e.target.value })}
                placeholder="Brief description of this objective"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowItemDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveItem} 
              disabled={saving}
              className="bg-red-600 hover:bg-red-700"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PurposeManagement;
